import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Drawer } from "../components/pele/Drawer";
import { ESCALA } from "../lib/escalaTipografica";
import { ajudaDaRota } from "./registroDeAjuda";

/**
 * O "?" QUE EXPLICA A TELA EM QUE VOCÊ ESTÁ.
 *
 * 👑 02/09: *"acesso assistido em cada página, para explicar o que faz cada uma
 * delas"*. Desbloqueado 28/10 (a trava "a estrutura/glossário vai mudar" caiu:
 * o redesign e o glossário único já foram entregues).
 *
 * ─── TRÊS DECISÕES, E CADA UMA EVITA UM DEFEITO CONHECIDO ───────────────────
 *
 * 1. **Descobre a tela pela ROTA**, não por quem o chamou. Por isso ele mora no
 *    `Layout` e as telas ganham ajuda sem serem tocadas — nenhuma sessão
 *    precisa mexer na tela da outra pra isso existir.
 *
 * 2. **A mecânica é do `Drawer` da casa**, não artesanal: foco preso, Esc,
 *    `aria-modal` e rolagem travada vêm de graça. Gaveta artesanal já custou uma
 *    auditoria de CX inteira aqui (29/07) — não repetir.
 *
 * 3. **Rota sem ajuda escrita → o botão SOME.** Um "?" que abre vazio é pior que
 *    "?" nenhum: ensina a pessoa que ajuda aqui não funciona, e aí ela não clica
 *    mais nas telas onde funciona.
 *
 * ⚠️ NÃO É TOUR. Não aponta botão nem ensina caminho — botão muda de lugar e a
 * ajuda apodreceria igual ao vídeo que foi descartado. Ele responde "o que é
 * esta tela, o que se faz aqui, e o que NÃO se faz aqui".
 */
export function BotaoAjuda() {
  const { pathname } = useLocation();
  const [aberto, setAberto] = useState(false);
  const ajuda = ajudaDaRota(pathname);

  // Sem texto escrito pra esta rota, nada aparece. Ver decisão 3 acima.
  if (!ajuda) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setAberto(true)}
        aria-label={`O que é a tela ${ajuda.titulo}?`}
        title={`O que é a tela ${ajuda.titulo}?`}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <HelpCircle className="h-[18px] w-[18px]" aria-hidden="true" />
      </button>

      <Drawer
        open={aberto}
        onClose={() => setAberto(false)}
        title={ajuda.titulo}
        larguraClassName="max-w-sm"
      >
        <div className="flex flex-col gap-5 p-5">
          <section className="flex flex-col gap-1.5">
            <h3 className={`${ESCALA.micro} font-semibold uppercase tracking-wider text-muted-foreground`}>
              O que é
            </h3>
            <p className={`${ESCALA.corpo} leading-relaxed text-foreground`}>{ajuda.oQueE}</p>
          </section>

          <section className="flex flex-col gap-1.5">
            <h3 className={`${ESCALA.micro} font-semibold uppercase tracking-wider text-muted-foreground`}>
              Você normalmente vem aqui para
            </h3>
            <ul className={`${ESCALA.corpo} flex flex-col gap-1.5 leading-relaxed text-foreground`}>
              {ajuda.vimAqui.map((item) => (
                <li key={item} className="flex gap-2">
                  <span aria-hidden="true" className="text-muted-foreground">
                    ·
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/*
            🔑 O campo que mais evita chamado. Toda tela tem uma vizinha com que
            é confundida, e dizer isso em voz alta poupa a viagem inteira.
          */}
          <section className="flex flex-col gap-1.5 rounded-md border border-border bg-muted/40 p-3">
            <h3 className={`${ESCALA.micro} font-semibold uppercase tracking-wider text-muted-foreground`}>
              O que esta tela NÃO faz
            </h3>
            <p className={`${ESCALA.corpo} leading-relaxed text-foreground`}>{ajuda.naoFaz}</p>
          </section>

          {ajuda.cuidado ? (
            <section className="flex flex-col gap-1.5 rounded-md border border-risk/30 bg-risk/5 p-3">
              <h3 className={`${ESCALA.micro} font-semibold uppercase tracking-wider text-risk`}>
                Cuidado
              </h3>
              <p className={`${ESCALA.corpo} leading-relaxed text-foreground`}>{ajuda.cuidado}</p>
            </section>
          ) : null}
        </div>
      </Drawer>
    </>
  );
}
